import { usePermissao } from '@/utils/permissao';
import { AnexosPanel } from '@/components/anexos/AnexosPanel';
import { Formik, FormikProps } from 'formik';
import React, { useEffect, useState } from 'react';
import * as Yup from 'yup';
import { useParams } from 'react-router-dom';
import { useVoltar } from '@/utils/voltar';
import { toast } from 'sonner';
import { MessageSquareText, X } from 'lucide-react';
import { AxiosError } from 'axios';
import { SinistroService, FichaService } from '../../services';
import { formatDate } from '../../utils/DateUtils';
import { SinistroDTO } from '@/types/SinistroDTO';
import { StatusSinistroEnum, StatusSinistroValues } from '@/types/enum/StatusSinistro';
import { Row } from '@/components/grid/row';
import useAuth from '@/components/hooks/use.auth';
import FormattedDate from '@/components/ui/formatted-date';
import { FormikInputDate } from '@/components/ui/formik-input';
import { FormikInput } from '@/components/ui/input';
import { FormikSelect } from '@/components/ui/select';
import { SectionTitle } from '@/components/ui/section-title';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { EntityIcon } from '@/components/ui/cores';
import { PageHeader } from '@/components/ui/page-header';
import { FormActions } from '@/components/ui/form-actions';
import { getTenant } from '@/utils/TenantUtils';
import { ErrorPage } from '../ErrorPage';

export const sinistroFormValidationSchema = Yup.object().shape({
    data: Yup.string().required('Informe a data do sinistro'),
    status: Yup.string().required('Selecione a situação')
});

const novoSinistro = (): SinistroDTO => ({
    status: StatusSinistroEnum.pendente,
    data: formatDate(new Date()),
    seguro: undefined,
    ocorrencias: []
});

const Resumo = ({ titulo, children }: { titulo: string; children: React.ReactNode }) => (
    <div className="min-w-0">
        <dt className="text-xs text-muted-foreground">{titulo}</dt>
        <dd className="truncate text-sm font-medium">{children || '—'}</dd>
    </div>
);

const SinistroFormPage: React.FC = () => {
    const somenteLeitura = !usePermissao('SEGURO_WRITE');
    const [sinistro, setSinistro] = useState<SinistroDTO>(novoSinistro);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<AxiosError>();
    const [novaOcorrencia, setNovaOcorrencia] = useState('');
    const voltar = useVoltar();
    const { idseguro, idsinistro } = useParams();
    const { user } = useAuth();
    const novo = idsinistro === 'novo';

    useEffect(() => {
        if (!idseguro || !idsinistro) return;
        setLoading(true);
        const busca = novo
            ? FichaService.getByCodigo(idseguro).then(({ data }) => setSinistro({ ...novoSinistro(), seguro: data }))
            : SinistroService.getByCodigo(idsinistro).then(({ data }) => setSinistro(data));
        busca.catch(setError).finally(() => setLoading(false));
    }, [idseguro, idsinistro, novo]);

    const onSave = (values: SinistroDTO) => {
        setLoading(true);
        const pendente = novaOcorrencia.trim();
        const dados = pendente ? { ...values, ocorrencias: [{ observacao: pendente }, ...values.ocorrencias] } : values;
        (values.id ? SinistroService.update(dados, values.id) : SinistroService.create(dados))
            .then(() => {
                toast.success(values.id ? 'Sinistro alterado com sucesso.' : 'Sinistro cadastrado com sucesso.');
                voltar(`/${getTenant()}/sinistro`);
            })
            .catch(() => {})
            .finally(() => setLoading(false));
    }

    const adicionarOcorrencia = (formik: FormikProps<SinistroDTO>) => {
        const observacao = novaOcorrencia.trim();
        if (!observacao) return;
        formik.setFieldValue('ocorrencias', [{ observacao, usuario: user?.id ? { id: user.id, nome: user.nome || '' } : undefined }, ...formik.values.ocorrencias]);
        setNovaOcorrencia('');
    }

    const removerOcorrencia = (formik: FormikProps<SinistroDTO>, index: number) =>
        formik.setFieldValue('ocorrencias', formik.values.ocorrencias.filter((_, i) => i !== index));

    if (error) {
        return <ErrorPage axiosError={error} />
    }

    const seguro = sinistro.seguro;

    return (
        <div className="space-y-6">
            <PageHeader icon={<EntityIcon entidade="sinistro" />} title={somenteLeitura ? 'Sinistro' : novo ? 'Novo sinistro' : 'Editar sinistro'} />
            <Card className='p-6 shadow-none'>
                <Formik initialValues={sinistro}
                    validationSchema={sinistroFormValidationSchema}
                    enableReinitialize
                    onSubmit={onSave}>
                    {(formikProps: FormikProps<SinistroDTO>) => (
                        <>
                        <dl className="mb-6 grid grid-cols-2 gap-x-6 gap-y-3 rounded-lg bg-muted/50 p-4 sm:grid-cols-5">
                            <div className="col-span-2"><Resumo titulo="Segurado">{seguro?.segurado?.nome}</Resumo></div>
                            <Resumo titulo="Seguradora">{seguro?.seguradora?.nome}</Resumo>
                            <Resumo titulo="Placa">{seguro?.placa || seguro?.descricao}</Resumo>
                            <Resumo titulo="Vigência">
                                {seguro?.vigenciaFinal && <>até <FormattedDate value={seguro.vigenciaFinal} /></>}
                            </Resumo>
                        </dl>
                        <fieldset disabled={somenteLeitura} className="min-w-0">
                        <Row>
                            <FormikInputDate name='data' label='Data do sinistro' size={4} />
                            <FormikInput name='aviso' label='Número do aviso' size={4} maxLength={50} placeholder='Informado pela seguradora' />
                            <FormikSelect
                                name='status'
                                itens={StatusSinistroValues}
                                getOptionLabel={value => value.descricao}
                                getOptionValue={value => value.codigo}
                                label='Situação'
                                allowEmptySelection={false}
                                size={4}
                            />
                        </Row>

                        <SectionTitle icon={<MessageSquareText />} cor="bg-rose-500/10 text-rose-600 dark:text-rose-400"
                            description="Registre cada contato com a seguradora, oficina ou segurado.">
                            Acompanhamento
                        </SectionTitle>
                        {!somenteLeitura && (
                        <div className="space-y-2">
                            <Textarea
                                aria-label="Nova ocorrência"
                                placeholder="Ex.: Vistoria agendada para segunda-feira"
                                rows={3}
                                value={novaOcorrencia}
                                onChange={e => setNovaOcorrencia(e.target.value)}
                                onKeyDown={e => {
                                    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) adicionarOcorrencia(formikProps);
                                }}
                            />
                            <div className="flex justify-end">
                                <Button type="button" variant="outline" disabled={!novaOcorrencia.trim()} onClick={() => adicionarOcorrencia(formikProps)}>
                                    Adicionar ocorrência
                                </Button>
                            </div>
                        </div>
                        )}

                        {formikProps.values.ocorrencias.length === 0 ? (
                            <p className="mb-2 mt-4 text-sm text-muted-foreground">Nenhuma ocorrência registrada.</p>
                        ) : (
                            <ol className="mb-2 mt-4 space-y-0">
                                {formikProps.values.ocorrencias.map((item, index) => (
                                    <li key={item.id || `nova-${index}`} className="relative border-l-2 border-muted pb-5 pl-5 last:pb-1">
                                        <span className={`absolute -left-[5px] top-1.5 h-2 w-2 rounded-full ${item.id ? 'bg-rose-500' : 'bg-primary'}`} />
                                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                            <span className="font-medium text-foreground">{item.usuario?.nome || 'Você'}</span>
                                            <span>
                                                {item.data
                                                    ? new Date(item.data).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' })
                                                    : 'será registrada ao salvar'}
                                            </span>
                                            {!item.id && (
                                                <button type="button" aria-label="Remover ocorrência" onClick={() => removerOcorrencia(formikProps, index)}
                                                    className="ml-auto rounded p-1 hover:bg-muted hover:text-foreground">
                                                    <X className="h-3.5 w-3.5" />
                                                </button>
                                            )}
                                        </div>
                                        <p className="mt-1 whitespace-pre-line text-sm">{item.observacao}</p>
                                    </li>
                                ))}
                            </ol>
                        )}
                        </fieldset>

                        <AnexosPanel entidade="sinistro" registro={sinistro.id} embutido somenteLeitura={somenteLeitura} />
                        <FormActions loading={loading} onSave={formikProps.submitForm} somenteLeitura={somenteLeitura} />
                        </>
                    )}
                </Formik>
            </Card>
        </div>
    );
}

export default SinistroFormPage;
